import React, { useEffect, useState, useRef } from 'react'
import Slider from 'react-slick'
import Helpers from '../../Helpers/Helpers'
import { Link } from 'react-router-dom'
import './mobile.css'

const PopularProducts = () => {
  const helpers = Helpers()
  const sliderRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [popularProducts, setPopularProducts] = useState([])
  const [productRatings, setProductRatings] = useState({})

  // Detect mobile viewport
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 767.98px)').matches)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767.98px)')
    const listener = () => setIsMobile(media.matches)
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [])

  // Rating display logic (similar to AstrologerCard)
  const renderRatingStars = (rating = 0) => {
    return (
      <div className="stars-container">
        {Array.from({ length: 5 }).map((_, idx) => {
          const difference = rating - idx

          if (difference >= 1) {
            return <span key={idx} className="star">★</span>
          } else if (difference > 0 && difference <= 1) {
            return <span key={idx} className="star half">★</span>
          } else {
            return <span key={idx} className="star empty">★</span>
          }
        })}
      </div>
    )
  }

  const formatRating = (rating) => {
    return Number(rating).toFixed(1)
  }

  // Calculate discount percentage using del_price and product_amount
  const calculateDiscountPercentage = (delPrice, productAmount) => {
    // Try del_price first, then fall back to product_amount
    const originalPrice = delPrice || productAmount
    const currentPrice = productAmount || delPrice

    if (!originalPrice || !currentPrice || originalPrice <= currentPrice) return 0
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
  }

  // Get the best pricing info from available fields
  const getPricingInfo = (product) => {
    const currentPrice = product.price || product.product_amount
    const originalPrice = product.del_price || product.original_price
    const discountPercentage = calculateDiscountPercentage(originalPrice, currentPrice)

    return {
      currentPrice,
      originalPrice,
      discountPercentage,
      hasDiscount: discountPercentage > 0
    }
  }

  // Fetch ratings for products
  const fetchProductRatings = async (productIds) => {
    const ratings = {}

    await Promise.all(
      productIds.map(async (productId) => {
        try {
          const response = await helpers.httpRequest(`/ratings/${productId}`, 'GET')
          if (response.status === 'success') {
            ratings[productId] = response.data
          }
        } catch (error) {
          console.error(`Error fetching rating for product ${productId}:`, error)
        }
      })
    )

    setProductRatings(ratings)
  }

  useEffect(() => {
    const fetchPopularProducts = async () => {
      try {
        // Calculate date range: last 30 days
        const toDate = new Date()
        const fromDate = new Date()
        fromDate.setDate(toDate.getDate() - 30)
        const format = d => d.toISOString().split('T')[0]

        const query = `/order-payment?from_date=${format(fromDate)}&to_date=${format(toDate)}`
        const orderRes = await helpers.httpRequest(query, 'GET')

        if (orderRes.status !== 'success') {
          throw new Error(orderRes.message || 'Failed to fetch order data')
        }

        const orders = orderRes.data || []
        // Aggregate product frequency and keep reference of first occurrence to pick image & title
        const freqMap = {}
        const productMap = {}

        orders.forEach(record => {
          const id = record.product_id
          if (!id) return
          freqMap[id] = (freqMap[id] || 0) + (record.quantity || 1)
          if (!productMap[id]) productMap[id] = record // keep the first instance
        })

        // Pick top 6 products by frequency
        const popular = Object.keys(freqMap)
          .sort((a, b) => freqMap[b] - freqMap[a])
          .slice(0, 6)
          .map(id => ({ ...productMap[id], orderCount: freqMap[id] }))

        setPopularProducts(popular)
        // Skipping rating API calls as per new requirement

      } catch (err) {
        console.error(err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPopularProducts()
  }, [])

  const CustomPrevArrow = ({ onClick }) => (
    <button className="custom-carousel-btn custom-carousel-prev" onClick={onClick} type="button">
      <i className="fas fa-chevron-left"></i>
    </button>
  )

  const CustomNextArrow = ({ onClick }) => (
    <button className="custom-carousel-btn custom-carousel-next" onClick={onClick} type="button">
      <i className="fas fa-chevron-right"></i>
    </button>
  )

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 3 } },
      { breakpoint: 992, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: "unslick" }
    ]
  }

  const renderCard = (product, idx) => {
    const productId = product._id || product.product_id
    // Ratings disabled for current iteration
    const rating = null
    const { currentPrice, originalPrice, discountPercentage, hasDiscount } = getPricingInfo(product)

    return (
      <div key={productId || idx} >
        <Link to={`/products/view/${productId}`} className="text-decoration-none">
          <div className="product-card h-100 text-center position-relative">
            {/* OFF Price Tag Above Image */}
            {hasDiscount && (
              <div className="off-price-tag">
                {discountPercentage}% OFF
              </div>
            )}


            {/* Discount Badge */}
            {/* {hasDiscount && (
              <div className="discount-badge">
                {discountPercentage}% OFF
              </div>
            )} */}

            {/* Product Image */}
            <img
              className="card-img"
              alt={product.title}
              src={(() => {
                if (product.full_main_image) return product.full_main_image
                if (product?.main_image) {
                  if (typeof product.main_image === 'object' && product.main_image.url) {
                    return `${process.env.REACT_APP_API_URL}/uploads/products/${product.main_image.url}`
                  }
                  if (typeof product.main_image === 'string') {
                    return `${process.env.REACT_APP_API_URL}/uploads/products/${product.main_image}`
                  }
                }
                if (product.full_image) return product.full_image
                return '/assets/images/no-image.png'
              })()}
            />

            {/* Product Details */}
            <div className="product-details">
              <h4 className="card-name">{product.title}</h4>

              {/* Price */}
              {isMobile ? (
                currentPrice && (
                  <div className="mobile-price">₹{currentPrice}</div>
                )
              ) : (
              <div className="price-section">
                {currentPrice && (
                  <div className="price-container">
                    <div className="price-row">
                      <span className="current-price">₹{currentPrice}</span>
                      {originalPrice && originalPrice > currentPrice && (
                        <span className="original-price">₹{originalPrice}</span>
                      )}
                    </div>
                    {hasDiscount && (
                      <div className="savings-info">
                        <span className="savings-text">You save ₹{originalPrice - currentPrice}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Minimal details on mobile – rating & purchase info removed */}
              {!isMobile && (
              <div className="rating-purchase-row">
                {/* Rating Section */}
                <div className="rating-section">
                  <div className="rating-stars">
                    {renderRatingStars(rating?.rating || 0)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>
    )
  }

  if (loading) {
    return (
      <section className="as_popular_products_wrapper as_padderTop80 as_padderBottom80">
        <div className="container text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </section>
    )
  }

  if (error || popularProducts.length === 0) {
    return (
      <section className="as_popular_products_wrapper as_padderTop80 as_padderBottom80">
        <div className="container text-center">
          <h3>{error ? 'Failed to load popular products' : 'No popular products found'}</h3>
        </div>
      </section>
    )
  }

  return (
    <section className="as_popular_products_wrapper  as_padderBottom80">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center">
            <h1 className="as_heading as_heading_center">Popular Products</h1>
          </div>
        </div>
        {/* <div className="row"> */}
          {/* Desktop View */}
          <div className="col-12">
            <div className="v3_blog_wrapper position-relative">
              <div className="row text-left d-none d-md-block">
                <Slider {...settings} ref={sliderRef}>
                  {popularProducts.map((product, idx) => renderCard(product, idx))}
                </Slider>
              </div>
            </div>

            {/* Mobile View */}
            <div className="d-md-none">
              <div className="popular-products-grid">
                {popularProducts.map((product, idx) => (
                  <div key={idx} className="product-grid-item">
                    {renderCard(product, idx)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        {/* </div> */}
      </div>
    </section>
  )
}

export default PopularProducts 